import React, { useState } from 'react';
import {
  FileText,
  BarChart3,
  Search,
  Settings,
  Brain
} from 'lucide-react';
import { theme } from '../../styles/theme';
import DocumentUpload from '../../components/Harmony/DocumentUpload';
import DocumentList from '../../components/Harmony/DocumentList';
import HarmonySettings from '../../components/Harmony/HarmonySettings';
import KnowledgeGraph from '../../components/Harmony/KnowledgeGraph';

const Harmony: React.FC = () => {
  const [activeTab, setActiveTab] = useState('documents');
  const [refreshDocuments, setRefreshDocuments] = useState(0);

  const handleUploadComplete = () => {
    // Trigger refresh of document list when upload completes
    setRefreshDocuments(prev => prev + 1);
    // Stay on documents tab to see the uploaded file
  };

  const tabs = [
    { id: 'documents', label: 'Document Management', icon: FileText },
    { id: 'knowledge', label: 'Knowledge Graph', icon: BarChart3 },
    { id: 'search', label: 'Semantic Search', icon: Search, disabled: true },
    { id: 'settings', label: 'Settings', icon: Settings },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1
            className="text-2xl font-bold flex items-center gap-2"
            style={{
              color: theme.colors.text.primary,
            }}
          >
            <Brain
              className="w-6 h-6"
              style={{ color: theme.colors.primary.main }}
            />
            Harmony Operational Knowledge Management
          </h1>
          <p
            className="text-sm mt-1"
            style={{ color: theme.colors.text.secondary }}
          >
            AI-powered document processing and knowledge graph system
          </p>
        </div>
      </div>

      {/* Tab Navigation */}
      <div
        className="flex space-x-1 p-1 rounded-lg"
        style={{
          backgroundColor: theme.colors.background.default,
        }}
      >
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => !tab.disabled && setActiveTab(tab.id)}
              disabled={tab.disabled}
              className={`flex-1 px-4 py-2 rounded-lg transition-colors flex items-center justify-center space-x-2 ${
                tab.disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
              }`}
              style={{
                backgroundColor:
                  activeTab === tab.id
                    ? theme.colors.background.paper
                    : "transparent",
                color:
                  activeTab === tab.id
                    ? theme.colors.primary.main
                    : tab.disabled
                      ? '#9ca3af' // gray-400 for disabled state
                      : theme.colors.text.secondary,
                boxShadow:
                  activeTab === tab.id ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
              }}
            >
              <Icon className="w-4 h-4" />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      <div
        className="rounded-xl p-6"
        style={{
          backgroundColor: theme.colors.background.paper,
          boxShadow: theme.widget.boxShadow,
        }}
      >
        {activeTab === 'documents' && (
          <div className="space-y-6">
            <div>
              <h2 className="text-xl font-semibold mb-4" style={{ color: theme.colors.text.primary }}>
                Document Management
              </h2>

              {/* Upload Section */}
              <div className="mb-8">
                <h3 className="text-lg font-medium mb-2" style={{ color: theme.colors.text.primary }}>
                  Upload Documents
                </h3>
                <p className="text-sm mb-4" style={{ color: theme.colors.text.secondary }}>
                  Upload documents to the knowledge base. The system will automatically process
                  and categorize them using AI.
                </p>
                <DocumentUpload onUploadComplete={handleUploadComplete} />
              </div>

              <div
                className="border-t"
                style={{ borderColor: theme.colors.border.light, margin: '2rem 0' }}
              />

              {/* Document List Section */}
              <div>
                <h3 className="text-lg font-medium mb-2" style={{ color: theme.colors.text.primary }}>
                  Document Library
                </h3>
                <p className="text-sm mb-4" style={{ color: theme.colors.text.secondary }}>
                  View and manage all documents in the knowledge base.
                </p>
                <DocumentList refreshTrigger={refreshDocuments} />
              </div>
            </div>
          </div>
        )}

        {activeTab === 'knowledge' && (
          <div style={{ height: '600px' }}>
            <KnowledgeGraph />
          </div>
        )}

        {activeTab === 'search' && (
          <div className="flex flex-col items-center justify-center py-12">
            <Search
              className="w-16 h-16 mb-4"
              style={{ color: theme.colors.text.secondary }}
            />
            <h3 className="text-lg font-medium mb-2" style={{ color: theme.colors.text.secondary }}>
              Semantic Search Coming Soon
            </h3>
            <p className="text-sm text-center" style={{ color: theme.colors.text.secondary }}>
              Search documents using natural language and AI-powered understanding.
            </p>
          </div>
        )}

        {activeTab === 'settings' && (
          <div>
            <h2 className="text-xl font-semibold mb-2" style={{ color: theme.colors.text.primary }}>
              Harmony Settings
            </h2>
            <p className="text-sm mb-4" style={{ color: theme.colors.text.secondary }}>
              Configure how documents are processed and stored in the knowledge graph.
            </p>
            <HarmonySettings />
          </div>
        )}
      </div>
    </div>
  );
};

export default Harmony;